import type {
  ApiKey, AuditEvent, Device, DeviceGroup, EnrollmentToken, Integration, Invitation, Invoice, Member, OrganizationSettings,
  OsFamily, Permission, Policy, Release, RemoteSession, Role, SessionRating, Subscription,
} from '../types';
import { PERMISSIONS } from '../types';

/** Générateur pseudo-aléatoire déterministe (mulberry32) : mêmes données de démo à chaque reset. */
export function createRng(seed: number) {
  let a = seed >>> 0;
  const next = () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  return {
    next,
    int: (min: number, max: number) => Math.floor(next() * (max - min + 1)) + min,
    pick: <T,>(arr: readonly T[]): T => arr[Math.floor(next() * arr.length)],
    chance: (p: number) => next() < p,
  };
}
type Rng = ReturnType<typeof createRng>;

export interface MockUser {
  id: string;
  email: string;
  name: string;
  twoFactorEnabled: boolean;
}

export interface OrgData {
  devices: Device[];
  groups: DeviceGroup[];
  members: Member[];
  roles: Role[];
  invitations: Invitation[];
  policies: Policy[];
  audit: AuditEvent[];
  sessions: RemoteSession[];
  /** Notes des clients (12 mois) ; les plus récentes proviennent des sessions notées. */
  ratings: SessionRating[];
  tokens: EnrollmentToken[];
  releases: Release[];
  subscription: Subscription;
  invoices: Invoice[];
  /** Moyens de paiement (jetons) ; créé à la demande depuis `subscription.paymentMethod`. */
  paymentMethods?: import('../types').PaymentMethod[];
  integrations: Integration[];
  apiKeys: ApiKey[];
  /** Zone politiques : historique des versions par politique (reconstitué à la demande). */
  policyVersions?: Record<string, import('../types').PolicyVersion[]>;
  /** Zones fleet / people : seuils d'alerte et configuration SSO (valeurs par défaut si absents). */
  alertSettings?: import('../types').AlertSettings;
  sso?: import('../types').SsoConfig | null;
}

export interface MockDb {
  /** Incrémentée quand le schéma change : une base persistée plus ancienne est régénérée. */
  version: 2;
  users: MockUser[];
  orgs: OrganizationSettings[];
  data: Record<string, OrgData>;
}

/** Mot de passe unique des comptes de démonstration. */
export const DEMO_PASSWORD = 'siraj-demo';
/** Code TOTP accepté par la simulation. */
export const DEMO_TOTP = '123456';

const DAY = 86_400_000;

const ROLE_PERMS: Record<string, Permission[]> = {
  owner: [...PERMISSIONS],
  admin: PERMISSIONS.filter((p) => p !== 'billing.manage'),
  supervisor: ['devices.read', 'devices.manage', 'groups.manage', 'members.read', 'policies.read', 'audit.read', 'sessions.read', 'sessions.recordings', 'ratings.read', 'settings.read'],
  technician: ['devices.read', 'sessions.read'],
  billing: ['billing.read', 'billing.manage', 'members.read', 'settings.read'],
  // Équivalent démo du rôle « auditor » du backend : consulte aussi la satisfaction
  viewer: ['devices.read', 'policies.read', 'sessions.read', 'ratings.read'],
};

const ROLE_META: Record<string, { name: string; description: string }> = {
  owner: { name: 'Propriétaire', description: 'Accès complet, y compris facturation et suppression de l\'organisation.' },
  admin: { name: 'Administrateur', description: 'Gère le parc, les personnes, les politiques et le déploiement.' },
  supervisor: { name: 'Superviseur', description: 'Surveille le parc, approuve les accès sensibles et relit les sessions.' },
  technician: { name: 'Technicien', description: 'Se connecte aux appareils qui lui sont attribués.' },
  billing: { name: 'Facturation', description: 'Consulte et gère l\'abonnement et les factures.' },
  viewer: { name: 'Lecture seule', description: 'Consulte le parc et les politiques sans rien modifier.' },
};

function buildRoles(members: Member[]): Role[] {
  return Object.keys(ROLE_PERMS).map((id) => ({
    id,
    name: ROLE_META[id].name,
    description: ROLE_META[id].description,
    system: true,
    permissions: ROLE_PERMS[id],
    memberCount: members.filter((m) => m.roleId === id).length,
  }));
}

export const MOCK_USERS: MockUser[] = [
  { id: 'u_amel', email: 'admin@tassili.dz', name: 'Amel Benyoucef', twoFactorEnabled: true },
  { id: 'u_karim', email: 'tech@tassili.dz', name: 'Karim Haddad', twoFactorEnabled: true },
  { id: 'u_nadia', email: 'supervisor@tassili.dz', name: 'Nadia Cherif', twoFactorEnabled: true },
  { id: 'u_sofiane', email: 'billing@tassili.dz', name: 'Sofiane Khelifi', twoFactorEnabled: false },
  { id: 'u_lina', email: 'viewer@tassili.dz', name: 'Lina Bouzid', twoFactorEnabled: false },
  { id: 'u_yacine', email: 'yacine@tassili.dz', name: 'Yacine Mebarki', twoFactorEnabled: true },
  { id: 'u_samira', email: 'samira@tassili.dz', name: 'Samira Belkacem', twoFactorEnabled: true },
  { id: 'u_walid', email: 'walid@tassili.dz', name: 'Walid Zerrouki', twoFactorEnabled: false },
  { id: 'u_meriem', email: 'meriem@tassili.dz', name: 'Meriem Saadi', twoFactorEnabled: true },
  { id: 'u_hocine', email: 'hocine@djurdjura.dz', name: 'Hocine Taleb', twoFactorEnabled: true },
  { id: 'u_imane', email: 'imane@djurdjura.dz', name: 'Imane Boudiaf', twoFactorEnabled: true },
];

const ORG1_MEMBERS: Array<[string, string]> = [
  ['u_amel', 'owner'], ['u_karim', 'technician'], ['u_nadia', 'supervisor'], ['u_sofiane', 'billing'], ['u_lina', 'viewer'],
  ['u_yacine', 'technician'], ['u_samira', 'technician'], ['u_walid', 'technician'], ['u_meriem', 'admin'],
];
const ORG2_MEMBERS: Array<[string, string]> = [['u_hocine', 'owner'], ['u_amel', 'admin'], ['u_imane', 'technician']];

const GROUP_DEFS: Array<[string, string, string, string | null]> = [
  ['grp_hq', 'Siège Alger', 'Postes du siège administratif', null],
  ['grp_oran', 'Agence Oran', 'Postes et caisses de l\'agence d\'Oran', null],
  ['grp_srv', 'Serveurs', 'Serveurs de production et de sauvegarde', null],
  ['grp_srv_db', 'Bases de données', 'Serveurs de bases de données', 'grp_srv'],
  ['grp_retail', 'Caisses magasins', 'Terminaux de caisse', null],
];

const DEVICE_PREFIX: Record<string, string[]> = {
  grp_hq: ['PC-COMPTA', 'PC-RH', 'PC-DIR', 'LT-COM', 'MBP-DESIGN'],
  grp_oran: ['PC-ORAN', 'LT-ORAN', 'PC-ACCUEIL'],
  grp_srv: ['SRV-APP', 'SRV-FILE', 'SRV-BKP', 'SRV-AD'],
  grp_srv_db: ['SRV-DB'],
  grp_retail: ['POS'],
};

const CPUS = ['Intel Core i5-1235U', 'Intel Core i7-12700', 'AMD Ryzen 5 5600U', 'Apple M2', 'Intel Xeon E-2336', 'Intel Core i3-10100'];
const OS_VERSIONS: Record<OsFamily, string[]> = {
  windows: ['Windows 11 23H2', 'Windows 10 22H2', 'Windows Server 2022'],
  macos: ['macOS 14.5', 'macOS 15.1'],
  linux: ['Ubuntu 22.04 LTS', 'Debian 12'],
  android: ['Android 14'],
  ios: ['iPadOS 17'],
};

/** Antivirus déterministe (sans tirage, pour ne pas décaler les données semées) : Defender, tiers, absent ou inconnu. */
function seedAntivirus(os: OsFamily, i: number): Pick<Device['inventory'], 'antivirusName' | 'antivirusStatus'> {
  if (os !== 'windows') return { antivirusName: '', antivirusStatus: 'unknown' };
  if (i % 11 === 0) return { antivirusName: '', antivirusStatus: 'unprotected' };
  if (i % 7 === 0) return { antivirusName: 'ESET Endpoint Security', antivirusStatus: 'protected' };
  return { antivirusName: 'Microsoft Defender', antivirusStatus: 'protected' };
}

function makeDevices(rng: Rng, count: number, now: number, groups: DeviceGroup[], idPrefix: string): Device[] {
  const devices: Device[] = [];
  const counters: Record<string, number> = {};
  for (let i = 0; i < count; i++) {
    const group = rng.pick(groups.length ? groups : [null as unknown as DeviceGroup]);
    const prefix = rng.pick(DEVICE_PREFIX[group?.id] ?? ['PC']);
    counters[prefix] = (counters[prefix] ?? 0) + 1;
    const isServer = prefix.startsWith('SRV');
    const os: OsFamily = isServer ? rng.pick(['windows', 'linux', 'windows'] as const) : prefix.startsWith('MBP') ? 'macos' : rng.chance(0.08) ? 'linux' : rng.chance(0.05) ? 'android' : 'windows';
    const online = rng.chance(isServer ? 0.95 : 0.62);
    const lastSeen = online ? now - rng.int(0, 90) * 1000 : now - rng.int(20, 60 * 24 * 14) * 60_000;
    const name = `${prefix}-${String(counters[prefix]).padStart(2, '0')}`;
    devices.push({
      id: `${idPrefix}_dev_${String(i + 1).padStart(4, '0')}`,
      sirajId: String(rng.int(100_000_000, 999_999_999)),
      name,
      hostname: name.toLowerCase(),
      os,
      osVersion: rng.pick(OS_VERSIONS[os]),
      status: online ? 'online' : 'offline',
      lastSeenAt: new Date(lastSeen).toISOString(),
      enrolledAt: new Date(now - rng.int(10, 400) * DAY).toISOString(),
      groupId: group?.id ?? null,
      tags: rng.chance(0.3) ? [rng.pick(['critique', 'VIP', 'pilote', 'télétravail'])] : [],
      agentVersion: rng.chance(0.82) ? '1.4.2' : rng.pick(['1.3.9', '1.4.0']),
      channel: rng.chance(0.9) ? 'stable' : 'beta',
      ipAddress: `10.${rng.int(0, 20)}.${rng.int(0, 255)}.${rng.int(2, 254)}`,
      inventory: {
        cpu: rng.pick(CPUS),
        ramGb: rng.pick([8, 16, 16, 32, 64]),
        diskGb: rng.pick([256, 512, 512, 1024, 2048]),
        ramUsedPct: rng.int(25, 97),
        diskUsedPct: rng.chance(0.12) ? rng.int(91, 99) : rng.int(20, 88),
        ...seedAntivirus(os, i),
      },
      openAlerts: 0,
    });
  }
  return devices;
}

const ACTIONS: Array<{ action: string; weight: number; targetType: string }> = [
  { action: 'session.start', weight: 30, targetType: 'device' },
  { action: 'session.end', weight: 28, targetType: 'device' },
  { action: 'file.transfer', weight: 8, targetType: 'device' },
  { action: 'auth.login', weight: 14, targetType: 'member' },
  { action: 'auth.login_failed', weight: 3, targetType: 'member' },
  { action: 'device.enroll', weight: 4, targetType: 'device' },
  { action: 'device.revoke', weight: 1, targetType: 'device' },
  { action: 'member.invite', weight: 2, targetType: 'member' },
  { action: 'policy.update', weight: 2, targetType: 'policy' },
  { action: 'session.denied', weight: 3, targetType: 'device' },
];

function makeAudit(rng: Rng, now: number, members: Member[], devices: Device[], policies: Policy[], count: number, idPrefix: string): AuditEvent[] {
  const total = ACTIONS.reduce((s, a) => s + a.weight, 0);
  const events: AuditEvent[] = [];
  for (let i = 0; i < count; i++) {
    let r = rng.next() * total;
    const def = ACTIONS.find((a) => (r -= a.weight) < 0) ?? ACTIONS[0];
    const member = rng.pick(members);
    const device = rng.pick(devices);
    const at = now - Math.floor(rng.next() ** 1.6 * 30 * DAY);
    const denied = def.action === 'session.denied' || def.action === 'auth.login_failed';
    const target =
      def.targetType === 'device'
        ? { type: 'device', id: device.id, name: device.name }
        : def.targetType === 'policy' && policies.length
          ? { type: 'policy', id: policies[0].id, name: policies[0].name }
          : { type: 'member', id: member.id, name: member.name };
    events.push({
      id: `${idPrefix}_aud_${String(i + 1).padStart(5, '0')}`,
      at: new Date(at).toISOString(),
      actor: { type: 'member', id: member.id, name: member.name },
      action: def.action,
      target,
      outcome: denied ? (def.action === 'session.denied' ? 'denied' : 'failure') : 'success',
      ip: `41.${rng.int(96, 111)}.${rng.int(0, 255)}.${rng.int(1, 254)}`,
      country: rng.chance(0.93) ? 'DZ' : rng.pick(['FR', 'TN', 'MA']),
      signatureValid: !rng.chance(0.004),
    });
  }
  return events.sort((a, b) => b.at.localeCompare(a.at));
}

const SUMMARIES = [
  'Imprimante réseau réinstallée et définie par défaut ; test d\'impression validé.',
  'Nettoyage du disque (38 Go libérés) et mise à jour Windows appliquée.',
  'Réinitialisation du profil Outlook corrompu, courrier resynchronisé.',
  'Diagnostic lenteur : service de sauvegarde bloqué, redémarré.',
  'Installation du client VPN et configuration du profil de l\'utilisateur.',
];

function makeSessions(rng: Rng, now: number, members: Member[], devices: Device[], count: number, idPrefix: string): RemoteSession[] {
  const techs = members.filter((m) => ['technician', 'admin', 'owner', 'supervisor'].includes(m.roleId));
  const sessions: RemoteSession[] = [];
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  for (let i = 0; i < count; i++) {
    const daysAgo = Math.floor(rng.next() ** 1.4 * 30);
    const dayStart = startOfToday.getTime() - daysAgo * DAY;
    const dow = new Date(dayStart).getDay();
    if ((dow === 5 || dow === 6) && rng.chance(0.55)) {
      i--;
      continue;
    }
    // Heures de bureau, jamais dans le futur
    let start = dayStart + rng.int(8 * 60, 17 * 60 + 30) * 60_000;
    if (start > now) start = now - rng.int(5, 240) * 60_000;
    const tech = rng.pick(techs);
    const device = rng.pick(devices);
    const durationSec = rng.int(4, 75) * 60;
    const active = daysAgo === 0 && now - start < 40 * 60_000 && rng.chance(0.25);
    const failed = !active && rng.chance(0.05);
    sessions.push({
      id: `${idPrefix}_ses_${String(i + 1).padStart(4, '0')}`,
      startedAt: new Date(start).toISOString(),
      endedAt: active ? null : new Date(start + durationSec * 1000).toISOString(),
      durationSec: active ? Math.floor((now - start) / 1000) : failed ? rng.int(5, 40) : durationSec,
      technicianId: tech.id,
      technicianName: tech.name,
      deviceId: device.id,
      deviceName: device.name,
      mode: rng.chance(0.25) ? 'unattended' : 'attended',
      status: active ? 'active' : failed ? 'failed' : 'ended',
      recorded: rng.chance(0.4),
      actions: rng.int(3, 120),
      bytesTransferred: rng.chance(0.4) ? rng.int(1, 900) * 1_000_000 : 0,
      aiSummary: !active && !failed && rng.chance(0.55) ? rng.pick(SUMMARIES) : undefined,
      clientRating: !active && !failed && rng.chance(0.6) ? rng.int(3, 5) : undefined,
      ticketRef: rng.chance(0.35) ? `GLPI-${rng.int(1000, 9999)}` : undefined,
    });
  }
  return sessions.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
}

const COMMENTS_GOOD = [
  'Très efficace, problème réglé en quelques minutes. Merci !',
  'Technicien patient et pédagogue, il explique chaque étape.',
  'Rapide et professionnel, rien à redire.',
  'Imprimante de nouveau fonctionnelle, merci beaucoup.',
  'Intervention claire, j\'ai pu reprendre mon travail tout de suite.',
  'Excellent suivi, il a même vérifié les sauvegardes.',
  'Très aimable et à l\'écoute.',
  'Problème Outlook résolu sans perte de données, parfait.',
];
const COMMENTS_MIXED = [
  'Problème résolu, mais l\'attente avant la prise en main est longue.',
  'Correct, il a fallu redémarrer deux fois le poste.',
  'Bonne intervention, j\'aurais aimé plus d\'explications.',
  'Le VPN fonctionne, mais la lenteur persiste un peu.',
];
const COMMENTS_BAD = [
  'Le problème revient dès le lendemain.',
  'Session coupée deux fois, il faut tout recommencer.',
  'Peu d\'explications, je ne sais pas ce qui a été modifié.',
  'Délai trop long pour un simple mot de passe.',
];

const commentFor = (rng: Rng, rating: number) => rng.pick(rating >= 4 ? COMMENTS_GOOD : rating === 3 ? COMMENTS_MIXED : COMMENTS_BAD);

/**
 * Notes des clients sur 12 mois. Les 30 derniers jours reprennent les sessions notées (avec commentaire éventuel
 * recopié dans `clientComment`) ; les mois plus anciens sont synthétiques (sessions hors fenêtre de la démo),
 * avec une tendance propre à chaque technicien pour rendre l'évolution mensuelle lisible.
 */
function makeRatings(rng: Rng, now: number, members: Member[], sessions: RemoteSession[], idPrefix: string): SessionRating[] {
  const ratings: SessionRating[] = [];
  let seq = 0;
  const id = () => `${idPrefix}_rat_${String(++seq).padStart(5, '0')}`;
  for (const se of sessions) {
    if (!se.clientRating) continue;
    const comment = rng.chance(0.5) ? commentFor(rng, se.clientRating) : null;
    if (comment) se.clientComment = comment;
    ratings.push({
      id: id(), technicianId: se.technicianId, technicianName: se.technicianName, rating: se.clientRating, comment,
      sessionId: se.id, createdAt: se.endedAt ?? se.startedAt,
    });
  }
  const techs = members.filter((m) => ['technician', 'admin', 'owner', 'supervisor'].includes(m.roleId));
  const windowStart = now - 31 * DAY;
  const current = new Date(now);
  for (const tech of techs) {
    const base = 3.3 + rng.next() * 1.3;
    const trend = (rng.next() - 0.5) * 0.12;
    for (let back = 11; back >= 1; back--) {
      const monthStart = Date.UTC(current.getUTCFullYear(), current.getUTCMonth() - back, 1);
      const monthEnd = Math.min(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() - back + 1, 1), windowStart);
      if (monthEnd <= monthStart) continue;
      const count = rng.chance(0.08) ? 0 : rng.int(3, 11);
      const mean = base + trend * (11 - back);
      for (let i = 0; i < count; i++) {
        const rating = Math.max(1, Math.min(5, Math.round(mean + (rng.next() - 0.5) * 2.4)));
        ratings.push({
          id: id(), technicianId: tech.id, technicianName: tech.name, rating,
          comment: rng.chance(0.4) ? commentFor(rng, rating) : null, sessionId: null,
          createdAt: new Date(monthStart + Math.floor(rng.next() * (monthEnd - monthStart))).toISOString(),
        });
      }
    }
  }
  return ratings.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

function defaultRules(): Policy['rules'] {
  return {
    clipboard: 'both', blockedFileTypes: [], maxTransferMb: null,
    privacyScreen: false, recordingRequired: false, accessWindows: [], requireApproval: false, approverRoleId: null,
  };
}

function makePolicies(now: number): Policy[] {
  return [
    {
      id: 'pol_default', name: 'Politique par défaut', description: 'Appliquée à tous les appareils sans politique spécifique.',
      enabled: true, dryRun: false, groupIds: [], roleIds: [], version: 3,
      updatedAt: new Date(now - 12 * DAY).toISOString(), updatedBy: 'Amel Benyoucef',
      rules: { ...defaultRules(), blockedFileTypes: ['.exe', '.bat', '.ps1'], maxTransferMb: 500 },
    },
    {
      id: 'pol_servers', name: 'Serveurs de production', description: 'Accès restreint aux heures ouvrées avec approbation et enregistrement.',
      enabled: true, dryRun: false, groupIds: ['grp_srv', 'grp_srv_db'], roleIds: ['technician'], version: 5,
      updatedAt: new Date(now - 3 * DAY).toISOString(), updatedBy: 'Meriem Saadi',
      rules: {
        ...defaultRules(), clipboard: 'none', recordingRequired: true, requireApproval: true, approverRoleId: 'supervisor',
        accessWindows: [{ days: [0, 1, 2, 3, 4], start: '08:00', end: '18:00' }], privacyScreen: true,
      },
    },
    {
      id: 'pol_retail', name: 'Caisses : transferts restreints', description: 'Limite les transferts de fichiers pour les liaisons 4G des magasins.',
      enabled: true, dryRun: true, groupIds: ['grp_retail'], roleIds: [], version: 1,
      updatedAt: new Date(now - 1 * DAY).toISOString(), updatedBy: 'Amel Benyoucef',
      rules: { ...defaultRules(), maxTransferMb: 50 },
    },
  ];
}

function makeInvoices(now: number, planPrice: number, currency: string, provider: Invoice['provider']): Invoice[] {
  return Array.from({ length: 8 }, (_, i) => {
    const d = new Date(now);
    d.setMonth(d.getMonth() - i, 1);
    return {
      id: `inv_${i + 1}`,
      number: `SIR-${d.getFullYear()}-${String(12 - i).padStart(4, '0')}`,
      issuedAt: d.toISOString(),
      amount: planPrice,
      currency,
      status: (i === 3 ? 'refunded' : i === 0 ? 'open' : 'paid') as Invoice['status'],
      provider,
    };
  });
}

function makeReleases(now: number): Release[] {
  return [
    { version: '1.5.0-beta.2', channel: 'beta', publishedAt: new Date(now - 2 * DAY).toISOString(), adoptionPct: 7, notes: 'Écran virtuel, reprise de transfert, correctifs capture macOS.' },
    { version: '1.4.2', channel: 'stable', publishedAt: new Date(now - 16 * DAY).toISOString(), adoptionPct: 82, notes: 'Correctif presse-papiers, amélioration du rendu sur liaisons instables.' },
    { version: '1.4.0', channel: 'stable', publishedAt: new Date(now - 48 * DAY).toISOString(), adoptionPct: 12, notes: 'Politiques d\'administration et fenêtres horaires.' },
    { version: '1.3.9', channel: 'stable', publishedAt: new Date(now - 90 * DAY).toISOString(), adoptionPct: 6, notes: 'Correctifs de stabilité.' },
  ];
}

function buildOrgData(opts: {
  seed: number; now: number; idPrefix: string; memberDefs: Array<[string, string]>; deviceCount: number; auditCount: number;
  sessionCount: number; withGroups: boolean; subscription: Subscription; invoicePrice: number; currency: string; provider: Invoice['provider'];
}): OrgData {
  const rng = createRng(opts.seed);
  const { now } = opts;
  const members: Member[] = opts.memberDefs.map(([uid, roleId], i) => {
    const u = MOCK_USERS.find((x) => x.id === uid)!;
    return {
      id: u.id, email: u.email, name: u.name, roleId, groupIds: roleId === 'technician' ? [rng.pick(GROUP_DEFS)[0]] : [],
      status: 'active', twoFactorEnabled: u.twoFactorEnabled, lastSeenAt: new Date(now - rng.int(1, 3000) * 60_000).toISOString(),
      createdAt: new Date(now - (200 - i * 12) * DAY).toISOString(),
    };
  });
  const groups: DeviceGroup[] = opts.withGroups ? GROUP_DEFS.map(([id, name, description, parentId]) => ({ id, name, description, parentId, deviceCount: 0 })) : [];
  const devices = makeDevices(rng, opts.deviceCount, now, groups, opts.idPrefix);
  for (const g of groups) g.deviceCount = devices.filter((d) => d.groupId === g.id).length;
  const policies = makePolicies(now);
  const invitations: Invitation[] = [
    { id: `${opts.idPrefix}_inv_1`, email: 'nouveau.tech@tassili.dz', roleId: 'technician', groupIds: ['grp_hq'], invitedBy: members[0].name, createdAt: new Date(now - 1 * DAY).toISOString(), expiresAt: new Date(now + 6 * DAY).toISOString(), status: 'pending' },
    { id: `${opts.idPrefix}_inv_2`, email: 'stagiaire@tassili.dz', roleId: 'viewer', groupIds: [], invitedBy: members[0].name, createdAt: new Date(now - 12 * DAY).toISOString(), expiresAt: new Date(now - 5 * DAY).toISOString(), status: 'expired' },
  ];
  const tokens: EnrollmentToken[] = [
    { id: `${opts.idPrefix}_tok_1`, label: 'Déploiement Intune — siège', tokenPreview: 'sjt_9f3a…', groupId: 'grp_hq', channel: 'stable', createdAt: new Date(now - 9 * DAY).toISOString(), expiresAt: new Date(now + 21 * DAY).toISOString(), maxUses: 200, uses: 87, status: 'active', createdBy: members[0].name },
    { id: `${opts.idPrefix}_tok_2`, label: 'Agence Oran — avril', tokenPreview: 'sjt_41bc…', groupId: 'grp_oran', channel: 'stable', createdAt: new Date(now - 60 * DAY).toISOString(), expiresAt: new Date(now - 30 * DAY).toISOString(), maxUses: 30, uses: 30, status: 'exhausted', createdBy: members[0].name },
  ];
  const integrations: Integration[] = [
    { id: 'int_glpi', kind: 'glpi', name: 'GLPI', status: 'connected', connectedAt: new Date(now - 40 * DAY).toISOString() },
    { id: 'int_freshdesk', kind: 'freshdesk', name: 'Freshdesk', status: 'disconnected' },
    { id: 'int_zendesk', kind: 'zendesk', name: 'Zendesk', status: 'disconnected' },
    { id: 'int_jira', kind: 'jira_sm', name: 'Jira Service Management', status: 'disconnected' },
    { id: 'int_oidc', kind: 'sso_oidc', name: 'SSO OIDC (Entra ID / Google)', status: 'disconnected' },
    { id: 'int_saml', kind: 'sso_saml', name: 'SSO SAML 2.0', status: 'disconnected' },
    { id: 'int_scim', kind: 'scim', name: 'Provisionnement SCIM', status: 'disconnected' },
  ];
  const apiKeys: ApiKey[] = [
    { id: `${opts.idPrefix}_key_1`, name: 'Export BI nocturne', prefix: 'sk_live_a41f…', scopes: ['audit.read', 'sessions.read'], createdAt: new Date(now - 70 * DAY).toISOString(), createdBy: members[0].name, lastUsedAt: new Date(now - 6 * 3600_000).toISOString(), expiresAt: null },
  ];
  // Ordre des tirages inchangé : audit puis sessions
  const audit = makeAudit(rng, now, members, devices, policies, opts.auditCount, opts.idPrefix);
  const sessions = makeSessions(rng, now, members, devices, opts.sessionCount, opts.idPrefix);
  // Générateur distinct : les notes n'altèrent pas les autres données de démonstration
  const ratings = makeRatings(createRng(opts.seed + 7), now, members, sessions, opts.idPrefix);
  return {
    devices, groups, members, roles: buildRoles(members), invitations, policies,
    audit, sessions, ratings,
    tokens, releases: makeReleases(now), subscription: opts.subscription,
    invoices: makeInvoices(now, opts.invoicePrice, opts.currency, opts.provider), integrations, apiKeys,
  };
}

/** Construit la base de démonstration complète (deux organisations). */
export function createSeed(now = Date.now()): MockDb {
  const periodEnd = new Date(now + 17 * DAY).toISOString();
  const org1: OrganizationSettings = {
    id: 'org_tassili', name: 'Tassili Informatique', slug: 'tassili', plan: 'team', createdAt: new Date(now - 420 * DAY).toISOString(), region: 'mena',
    domain: 'tassili.dz', require2fa: true,
    whiteLabel: { enabled: false, primaryColor: '#F5B335' },
    security: { sessionTimeoutMin: 60, allowedCountries: ['DZ', 'FR'], blockedIps: [], trustedDevicesOnly: false },
    auditRetentionDays: 365,
  };
  const org2: OrganizationSettings = {
    id: 'org_djurdjura', name: 'Djurdjura Logistique', slug: 'djurdjura', plan: 'pro', createdAt: new Date(now - 130 * DAY).toISOString(), region: 'eu',
    domain: 'djurdjura.dz', require2fa: false,
    whiteLabel: { enabled: false, primaryColor: '#F5B335' },
    security: { sessionTimeoutMin: 120, allowedCountries: [], blockedIps: [], trustedDevicesOnly: false },
    auditRetentionDays: 90,
  };
  return {
    version: 2,
    users: MOCK_USERS,
    orgs: [org1, org2],
    data: {
      org_tassili: buildOrgData({
        seed: 1337, now, idPrefix: 'tas', memberDefs: ORG1_MEMBERS, deviceCount: 148, auditCount: 420, sessionCount: 260, withGroups: true,
        subscription: {
          planId: 'team', billingMode: 'per_technician', status: 'active', currentPeriodEnd: periodEnd,
          technicians: { used: 8, limit: 10 }, devices: { used: 148, limit: null }, aiCredits: { used: 1340, limit: 2000 },
          paymentMethod: { brand: 'cib', last4: '4821', expiry: '09/27' },
        },
        invoicePrice: 129, currency: 'EUR', provider: 'satim',
      }),
      org_djurdjura: buildOrgData({
        seed: 4242, now, idPrefix: 'dju', memberDefs: ORG2_MEMBERS, deviceCount: 36, auditCount: 120, sessionCount: 70, withGroups: false,
        subscription: {
          planId: 'pro', billingMode: 'per_device', status: 'trialing', currentPeriodEnd: periodEnd, trialEndsAt: new Date(now + 9 * DAY).toISOString(),
          technicians: { used: 2, limit: 3 }, devices: { used: 36, limit: 50 }, aiCredits: { used: 180, limit: 500 },
        },
        invoicePrice: 49, currency: 'EUR', provider: 'stripe',
      }),
    },
  };
}

/** Construit une organisation neuve et vide (inscription) avec son propriétaire comme seul membre. */
export function createEmptyOrg(owner: MockUser, orgName: string, now: number): { org: OrganizationSettings; data: OrgData } {
  const id = `org_${owner.id}_${now.toString(36)}`;
  const nowIso = new Date(now).toISOString();
  const member: Member = {
    id: owner.id, email: owner.email, name: owner.name, roleId: 'owner', groupIds: [], status: 'active',
    twoFactorEnabled: owner.twoFactorEnabled, lastSeenAt: nowIso, createdAt: nowIso,
  };
  const org: OrganizationSettings = {
    id, name: orgName, slug: id, plan: 'free', createdAt: nowIso, region: 'mena', require2fa: false,
    whiteLabel: { enabled: false, primaryColor: '#F5B335' },
    security: { sessionTimeoutMin: 60, allowedCountries: [], blockedIps: [], trustedDevicesOnly: false },
    auditRetentionDays: 90,
  };
  const data: OrgData = {
    devices: [], groups: [], members: [member], roles: buildRoles([member]), invitations: [], policies: [],
    audit: [{ id: `${id}_aud_1`, at: nowIso, actor: { type: 'member', id: owner.id, name: owner.name }, action: 'org.create', target: { type: 'org', id, name: orgName }, outcome: 'success', ip: '0.0.0.0', country: 'DZ', signatureValid: true }],
    sessions: [], ratings: [], tokens: [], releases: makeReleases(now),
    subscription: { planId: 'free', billingMode: 'per_technician', status: 'active', technicians: { used: 1, limit: 3 }, devices: { used: 0, limit: 10 }, aiCredits: { used: 0, limit: 100 }, currentPeriodEnd: new Date(now + 30 * DAY).toISOString() },
    invoices: [],
    integrations: [
      { id: 'int_glpi', kind: 'glpi', name: 'GLPI', status: 'disconnected' },
      { id: 'int_freshdesk', kind: 'freshdesk', name: 'Freshdesk', status: 'disconnected' },
      { id: 'int_zendesk', kind: 'zendesk', name: 'Zendesk', status: 'disconnected' },
      { id: 'int_jira', kind: 'jira_sm', name: 'Jira Service Management', status: 'disconnected' },
      { id: 'int_oidc', kind: 'sso_oidc', name: 'SSO OIDC (Entra ID / Google)', status: 'disconnected' },
      { id: 'int_saml', kind: 'sso_saml', name: 'SSO SAML 2.0', status: 'disconnected' },
      { id: 'int_scim', kind: 'scim', name: 'Provisionnement SCIM', status: 'disconnected' },
    ],
    apiKeys: [],
  };
  return { org, data };
}

export { ROLE_PERMS };
